import { useState, useMemo } from 'react';

import { Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { ChannelTestDialog } from '@/components/channels/ChannelTestDialog';
import { ConfigurationList } from '@/components/configurations/ConfigurationList';
import { TestCallDialog } from '@/components/configurations/TestCallDialog';
import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  useConfigurations,
  useDeleteConfiguration,
  useTestConfiguration,
  useUpdateConfiguration,
} from '@/hooks/useConfigurations';
import { usePageTitle } from '@/hooks/usePageTitle';
import { extractErrorMessage } from '@/lib/utils';

import type { ConfigurationSetup } from '@repo/shared-types';

export const ConfigurationSetupsPage = () => {
  usePageTitle('Configurations');
  const navigate = useNavigate();
  const { data: configs = [], isLoading } = useConfigurations();
  const deleteMutation = useDeleteConfiguration();
  const testMutation = useTestConfiguration();
  const updateMutation = useUpdateConfiguration();

  const [deleteTarget, setDeleteTarget] = useState<ConfigurationSetup | null>(null);
  const [testTarget, setTestTarget] = useState<ConfigurationSetup | null>(null);
  const [webTestTarget, setWebTestTarget] = useState<ConfigurationSetup | null>(null);
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredConfigs = useMemo(() => {
    if (!searchQuery.trim()) return configs;
    const lowerQuery = searchQuery.toLowerCase();
    return configs.filter((config) => config.name.toLowerCase().includes(lowerQuery));
  }, [configs, searchQuery]);

  const handleNewConfiguration = () => {
    navigate('/configurations/new');
  };

  const handleEditConfiguration = (config: ConfigurationSetup) => {
    navigate(`/configurations/${config.id}`);
  };

  const handleDeleteConfiguration = (config: ConfigurationSetup) => {
    setDeleteTarget(config);
  };

  const handleTestConfiguration = (config: ConfigurationSetup) => {
    setTestTarget(config);
  };

  const handleWebRtcTestConfiguration = (config: ConfigurationSetup) => {
    setWebTestTarget(config);
  };

  const handleToggleLock = async (config: ConfigurationSetup) => {
    const locking = !config.locked;
    try {
      await updateMutation.mutateAsync({ id: config.id, data: { locked: locking } });
      toast({
        variant: 'success',
        title: locking ? 'Configuration locked' : 'Configuration unlocked',
        description: locking
          ? `"${config.name}" can no longer be edited or deleted.`
          : `"${config.name}" can be edited again.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: locking ? 'Failed to lock configuration' : 'Failed to unlock configuration',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleTestCall = async (phoneNumber: string) => {
    if (!testTarget) return;
    try {
      setIsTesting(true);
      await testMutation.mutateAsync({
        id: testTarget.id,
        phoneNumber,
      });
      setTestTarget(null);
      toast({
        variant: 'success',
        title: 'Test call initiated',
        description: `Test call to ${phoneNumber} has been started successfully.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to initiate test call',
        description: extractErrorMessage(error),
      });
    } finally {
      setIsTesting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      toast({
        variant: 'success',
        title: 'Configuration deleted',
        description: `"${deleteTarget.name}" has been deleted successfully.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete configuration',
        description: extractErrorMessage(error),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading configurations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Configuration</p>
          <h1 className="text-3xl font-bold tracking-tight">Hybrid AI call parameters</h1>
          <p className="text-muted-foreground max-w-3xl">
            Define prompt templates, call windows, and retry policies that the scheduler uses when
            dispatching 14-day availability jobs.
          </p>
        </div>
        <Button onClick={handleNewConfiguration}>
          <Plus className="h-4 w-4" />
          New Configuration
        </Button>
      </header>

      <Card>
        <CardHeader className="flex flex-col gap-4 space-y-0 pb-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1.5">
            <CardTitle>Configurations</CardTitle>
            <CardDescription>
              Preview the prompt and scheduling constraints for each setup.
            </CardDescription>
          </div>
          <div className="relative w-full sm:w-[300px]">
            <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search configurations..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent>
          <ConfigurationList
            items={filteredConfigs}
            onEdit={handleEditConfiguration}
            onDelete={handleDeleteConfiguration}
            onTest={handleTestConfiguration}
            onTestWebrtc={handleWebRtcTestConfiguration}
            onToggleLock={handleToggleLock}
          />
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={Boolean(deleteTarget)}
        resourceName="configuration"
        resourceIdentifier={deleteTarget?.name}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
      />

      <TestCallDialog
        open={Boolean(testTarget)}
        onOpenChange={(open) => !open && setTestTarget(null)}
        configurationName={testTarget?.name || ''}
        onSubmit={handleTestCall}
        isLoading={isTesting}
      />

      <ChannelTestDialog
        open={Boolean(webTestTarget)}
        onOpenChange={(open) => !open && setWebTestTarget(null)}
        configurationId={webTestTarget?.id}
        configurationName={webTestTarget?.name || ''}
      />
    </div>
  );
};
